/**
 * Wire adapter: `ArtefactType` (`@heidi/evidence/contracts`) ->
 * `ExportArtefactKind` (`@heidi/agents`).
 *
 * App composition for the evidence->agents join; the domain DAG refuses this
 * edge by design — this adapter is the boundary contract.
 *
 * `DOMAIN_EDGES.agents` is `[]` and `DOMAIN_TYPE_CONTRIBUTIONS.agents` is
 * `['session']`, so `@heidi/agents` may not name the evidence door at all — not
 * even type-only, because every external a package source names has to be a
 * declared dependency and that declaration IS the edge. The app layer is the one
 * place both doors are importable, which is why the mapping lives here and why
 * its own compilation is what reports a member added or removed on either side.
 *
 * The map is TOTAL BY CONSTRUCTION rather than by review: a `Record` keyed on the
 * wire union fails `tsc` on a member added to it (missing key) and on one removed
 * from it (excess key), and the value position fails on a member the package
 * union drops. All seven arms map to the same spelling today, and that is a
 * measurement rather than a licence to collapse the map into a cast — the reason
 * the join is safe is this file, not the two lists happening to agree.
 *
 * `isPreviewableArtefactType` is the other export of the wire door and is NOT
 * mapped: the card renders an icon and a progress bar and reads no previewability.
 *
 * THE EVIDENCE ROOT AND NOT THE CONSUMING ONE, under rule 2's corner clause: the
 * consuming root is `projects`, which is LOCKED, and a locked root hosts no
 * adapter — every production module beneath it is frozen by path, and the
 * refusal's own recipe is to write it in `packages/agents/src/ui`, which this
 * standard forbids. The wire domain owns it instead, which is also the accurate
 * owner here: the one surface that needs this mapping sits under
 * `src/features/projects/timeline/evidence-tool-adapters/`, a sub-path
 * `src/features/projects/CLAUDE.md` records as a FOREIGN-DOMAIN PARTITION —
 * Evidence's work at an address under that root and outside its ratchet.
 *
 * MOVING THIS FILE BACK IS A GATE FAILURE, not a tidy-up: it fails
 * `pnpm migration:check` at module and export grain the moment it lands under
 * `src/features/projects/`.
 */
import type { ArtefactType } from '@heidi/evidence/contracts';

import type { ExportArtefactKind } from '@heidi/agents';

const KIND_BY_ARTEFACT_TYPE: Record<ArtefactType, ExportArtefactKind> = {
  csv: 'csv',
  excel: 'excel',
  word: 'word',
  pptx: 'pptx',
  pdf: 'pdf',
  patient_handout: 'patient_handout',
  form_fill: 'form_fill',
};

export function toExportArtefactKind(wire: ArtefactType): ExportArtefactKind {
  return KIND_BY_ARTEFACT_TYPE[wire];
}
